import React from 'react';
import { X, Package, CheckCircle, AlertTriangle } from 'lucide-react';

const BarcodeScannerOverlay = ({
  isVisible,
  onClose,
  scanResult,
  boxNumber,
  boxDimensions,
  boxName,
  currentBoxItems = [],
  shouldFlash = false
}) => {
  if (!scanResult) return null;

  const {
    success,
    item,
    newQuantity,
    totalUnitsInBox,
    boxWeight,
    weightStatus,
    message,
    remainingInShipment
  } = scanResult;

  // Get the first 6 other items in the box (excluding the current item)
  const otherItems = currentBoxItems
    .filter(boxItem => boxItem.ASIN !== item?.asin)
    .slice(0, 6);

  const getWeightStatusColor = () => {
    if (!weightStatus) return 'text-gray-500';
    switch (weightStatus) {
      case 'safe': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'alert': return 'text-orange-600';
      case 'critical': return 'text-red-600';
      default: return 'text-gray-500';
    }
  };

  const getWeightStatusBg = () => {
    if (!weightStatus) return 'bg-gray-100';
    switch (weightStatus) {
      case 'safe': return 'bg-green-100';
      case 'warning': return 'bg-yellow-100';
      case 'alert': return 'bg-orange-100';
      case 'critical': return 'bg-red-100';
      default: return 'bg-gray-100';
    }
  };

  const imageUrl = item?.images?.[0]?.large_url ||
    'https://qph.cf2.quoracdn.net/main-qimg-654617264f9192ec976abe6e53356240-lq';

  // Ensure boxWeight is a valid number
  const safeBoxWeight = boxWeight ? parseFloat(boxWeight) : 0;
  const isValidWeight = !isNaN(safeBoxWeight) && safeBoxWeight > 0;

  return (
    <>
      {isVisible && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
          onClick={onClose}
        >
          <div
            key={item?.fnsku || 'default'}
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 overflow-hidden"
            style={{
              backgroundColor: shouldFlash ? '#fef08a' : '#ffffff',
              transition: shouldFlash ? 'background-color 0.15s ease-in-out' : 'none'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`p-6 ${success ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-red-500 to-red-600'}`}>
              <div className="flex items-center justify-between text-white">
                <div className="flex items-center gap-4">
                  {success ? (
                    <CheckCircle className="w-12 h-12" />
                  ) : (
                    <AlertTriangle className="w-12 h-12" />
                  )}
                  <div>
                    <h2 className="text-3xl font-bold">
                      {success ? 'Item Added!' : 'Scan Failed'}
                    </h2>
                    <div className="flex items-center gap-3 mt-1">
                      <p className="text-white text-2xl font-bold">
                        Box #{boxNumber}
                      </p>
                      {boxName && (
                        <span className="text-green-100 text-base">
                          • {boxName}
                        </span>
                      )}
                      {boxDimensions && (
                        <span className="text-green-100 text-base">
                          • {boxDimensions}
                        </span>
                      )}
                    </div>
                    {!success && message && (
                      <p className="text-red-100 text-sm mt-1">{message}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {success && item ? (
              <div className="p-8">
                {/* Product Image and Details */}
                <div className="flex gap-6 mb-6">
                  {/* Large Product Image */}
                  <div className="flex-shrink-0">
                    <div className="w-64 h-64 bg-gray-100 rounded-lg overflow-hidden border-4 border-green-200 shadow-lg">
                      <img
                        src={imageUrl}
                        alt={item.title}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-3">
                        {item.title}
                      </h3>
                      <div className="space-y-1 text-sm text-gray-600 mb-4">
                        <p><span className="font-semibold">SKU:</span> {item.merchant_sku}</p>
                        <p><span className="font-semibold">FNSKU:</span> {item.fnsku}</p>
                        <p><span className="font-semibold">ASIN:</span> {item.asin}</p>
                      </div>
                    </div>

                    {/* Quantity Badge */}
                    <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                      <div className="text-center">
                        <div className="text-green-600 text-sm font-medium mb-1">
                          Quantity in Box #{boxNumber}
                        </div>
                        <div className="text-5xl font-bold text-green-700">
                          {newQuantity}
                        </div>
                        <div className="text-green-600 text-sm mt-1">
                          {newQuantity === 1 ? 'unit' : 'units'}
                        </div>
                        {remainingInShipment !== undefined && remainingInShipment !== null && (
                          <div className="mt-3 pt-3 border-t border-green-200">
                            <div className="text-xs text-gray-600 mb-1">
                              {remainingInShipment < 0 ? 'OVERAGE' : 'Remaining in Shipment'}
                            </div>
                            <div className={`text-2xl font-bold flex items-center justify-center gap-2 ${
                              remainingInShipment < 0 ? 'text-red-600' :
                              remainingInShipment === 0 ? 'text-gray-400' :
                              remainingInShipment <= 5 ? 'text-orange-600' :
                              'text-blue-600'
                            }`}>
                              {remainingInShipment < 0 && (
                                <AlertTriangle className="w-6 h-6" />
                              )}
                              {Math.abs(remainingInShipment)}
                            </div>
                            {remainingInShipment < 0 && (
                              <div className="text-xs text-red-600 font-semibold mt-1">
                                ⚠️ Scanned {Math.abs(remainingInShipment)} more than expected!
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Weight Meter */}
                {isValidWeight && (
                  <div className={`${getWeightStatusBg()} rounded-lg p-4 mb-6 ${
                    weightStatus === 'warning' || weightStatus === 'alert' || weightStatus === 'critical'
                      ? 'border-4'
                      : 'border-2'
                  } ${
                    weightStatus === 'warning' ? 'border-yellow-400' :
                    weightStatus === 'alert' ? 'border-orange-500' :
                    weightStatus === 'critical' ? 'border-red-600' :
                    'border-green-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700">Box Weight</span>
                      <span className={`text-xl font-bold ${getWeightStatusColor()}`}>
                        {safeBoxWeight.toFixed(2)} kg / 21 kg
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden shadow-inner">
                      <div
                        className={`h-full transition-all duration-500 ${
                          weightStatus === 'safe' ? 'bg-green-500' :
                          weightStatus === 'warning' ? 'bg-yellow-500' :
                          weightStatus === 'alert' ? 'bg-orange-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${Math.min((safeBoxWeight / 21) * 100, 100)}%` }}
                      />
                    </div>
                    {weightStatus === 'warning' && (
                      <p className="text-sm text-yellow-800 mt-2 font-semibold">⚠️ WARNING: Approaching 21kg weight limit! Current: {safeBoxWeight.toFixed(2)}kg</p>
                    )}
                    {weightStatus === 'alert' && (
                      <p className="text-base text-orange-800 mt-2 font-bold">⚠️ ALERT: Very close to 21kg weight limit! Current: {safeBoxWeight.toFixed(2)}kg</p>
                    )}
                    {weightStatus === 'critical' && (
                      <p className="text-lg text-red-800 mt-2 font-bold">❌ CRITICAL: Weight limit exceeded! Current: {safeBoxWeight.toFixed(2)}kg / Max: 21kg</p>
                    )}
                  </div>
                )}

                {/* Other Items in Box */}
                {otherItems.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Other Items in Box #{boxNumber} ({currentBoxItems.length - 1} more)
                    </h4>
                    <div className="grid grid-cols-6 gap-2">
                      {otherItems.map((boxItem, index) => (
                        <div
                          key={index}
                          className="relative aspect-square bg-gray-100 rounded border border-gray-200 overflow-hidden group"
                        >
                          <img
                            src={boxItem.IMAGE_URL || 'https://qph.cf2.quoracdn.net/main-qimg-654617264f9192ec976abe6e53356240-lq'}
                            alt={boxItem.TITLE || boxItem.SKU}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute bottom-0 right-0 bg-blue-600 text-white text-xs px-1 py-0.5 rounded-tl font-bold">
                            {boxItem.QTY}
                          </div>
                          {/* Tooltip on hover */}
                          <div className="absolute inset-0 bg-black/80 text-white text-xs p-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-center">
                            <span className="line-clamp-3">{boxItem.TITLE || boxItem.SKU}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Total Units Summary */}
                <div className="mt-6 text-center text-sm text-gray-500">
                  <p>Total units in this box: <span className="font-bold text-gray-700">{totalUnitsInBox}</span></p>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center">
                <AlertTriangle className="w-20 h-20 text-red-500 mx-auto mb-4" />
                <p className="text-lg text-gray-700">{message || 'Unable to process scan'}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BarcodeScannerOverlay;
